/**
 * Qual das linhas de produto uma trilha é — agora são três, não duas.
 *
 *   CURSO                       catálogo aberto (free e pro), linha 'curso'
 *   TREINAMENTO CORPORATIVO     genérico, linha 'treinamento' + visibility 'public':
 *                               entra com o Pro individual e com o plano de empresa.
 *                               Quem está no free VÊ a listagem, com cadeado.
 *   TREINAMENTO PERSONALIZADO   linha 'treinamento' + visibility 'restricted':
 *                               material construído para um cliente. Só entra por
 *                               código de resgate, concessão do backoffice ou vínculo
 *                               da empresa — e a API nem devolve o título para quem
 *                               não tem acesso.
 *
 * Esta regra estava copiada em quatro arquivos (Dashboard, TrackDetail, Sidebar e
 * LandingPage), cada um com a sua variação: uns testavam só `visibility`, outros
 * também a flag `restricted`, e a LandingPage testava a negação. Divergir aqui vaza
 * título de treinamento para quem não tem acesso, então a regra vive em um lugar só.
 * Use sempre estes helpers; não escreva `visibility === 'restricted'` solto.
 *
 * O `|| restricted === true` é fallback: só a API de admin devolve essa flag booleana.
 * O `|| visibility === 'restricted'` em ehTreinamento também é fallback, para payload
 * antigo (gravado antes de o campo `linha` existir) continuar caindo na lista certa.
 */
export function ehTreinamento(track) {
  return track?.linha === 'treinamento'
    || track?.visibility === 'restricted'
    || track?.restricted === true;
}

export function ehCurso(track) {
  return !ehTreinamento(track);
}

/**
 * Restrita = material de um cliente específico. Separa o treinamento personalizado do
 * corporativo genérico, que é público e pago. Não confunda com estaBloqueada(): uma
 * trilha restrita que chegou no payload JÁ é acessível para quem a recebeu.
 */
export function ehRestrita(track) {
  return track?.visibility === 'restricted' || track?.restricted === true;
}

/**
 * Quem decide se o conteúdo abre é o SERVIDOR, e ele manda pronto em `bloqueada`.
 * A conta local antiga (tier === 'pro' && userTier === 'free') passou a estar errada:
 * o assento de uma empresa com plano ativo continua com tier 'free' no usuário e mesmo
 * assim abre curso Pro. Não recalcule aqui.
 */
export function estaBloqueada(track) {
  return track?.bloqueada === true;
}

/**
 * Copy da parede de conteúdo pago, num lugar só porque ela aparece em cinco telas
 * (catálogo, início, detalhe da trilha, player e prova) e divergir entre elas faz o
 * aluno achar que tropeçou em erros diferentes.
 *
 * Dois cenários bem diferentes se escondem atrás do mesmo cadeado:
 *  - ninguém pagando: caminho é assinar o Pro ou contratar um plano de empresa;
 *  - empresa inadimplente/cancelada: o time voltou ao comportamento free e quem
 *    resolve é o gestor. Aí não faz sentido oferecer "Assinar Pro" ao colaborador —
 *    seria pedir que ele pague do próprio bolso o que a empresa já contratou.
 */
export function paredeDeAcesso(user) {
  const empresa = user?.empresa || null;
  const situacao = empresa?.situacao;
  const nomeEmpresa = empresa?.nome || 'sua empresa';
  const ehGestor = empresa?.papel === 'gestor';

  if (empresa && (situacao === 'inadimplente' || situacao === 'cancelada')) {
    const encerrado = situacao === 'cancelada';
    const acoes = ehGestor
      ? [{ rotulo: 'Ver plano da empresa', para: '/empresa', icone: 'bi-building', principal: true }]
      : [];
    return {
      situacao,
      titulo: encerrado ? 'Plano da empresa encerrado' : 'Plano da empresa com pagamento pendente',
      texto: `${encerrado
        ? `O plano de ${nomeEmpresa} foi encerrado`
        : `O plano de ${nomeEmpresa} está com pagamento pendente`}, então os cursos Pro e os treinamentos ficam bloqueados para o time. Os cursos gratuitos seguem abertos e os certificados já emitidos continuam disponíveis. ${ehGestor
        ? 'Regularize o plano para liberar o acesso de novo.'
        : 'Quem regulariza é o gestor da conta na sua empresa.'}`,
      curto: encerrado
        ? 'O plano da sua empresa foi encerrado.'
        : 'O plano da sua empresa está com pagamento pendente.',
      acoes,
    };
  }

  return {
    situacao: 'assinatura',
    titulo: 'Conteúdo para assinantes',
    texto: 'Este conteúdo é para assinantes Pro ou para empresas com plano ativo.',
    curto: 'Este conteúdo é para assinantes Pro ou para empresas com plano ativo.',
    acoes: [
      { rotulo: 'Assinar Pro', para: '/assinatura', icone: 'bi-star-fill', principal: true },
      { rotulo: 'Planos para empresas', para: '/assinatura?modo=empresas', icone: 'bi-building', principal: false },
    ],
  };
}
